import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  LinearProgress,
  Stack,
  Divider,
} from "@mui/material";
import {
  AccessTime,
  BugReport,
  CheckCircle,
  PlayArrow,
  Schedule,
  Lightbulb,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { supabase } from "../supabaseClient";
import axios from "axios";
import { API_URL } from "../globals";
import { format } from "date-fns";

const Runs = () => {
  const [user, setUser] = useState(null);
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState("");

  const setSelectedStudioWithLogging = (studioId) => {
    console.log('Runs - setSelectedStudio called with:', studioId);
    setSelectedStudio(studioId);
  };
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const navigate = useNavigate();

  async function getUser() {
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/user`, {
        headers: headers,
      });

      if (response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getAvailableStudios() {
    console.log('Runs - getAvailableStudios called');
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/studio/seeds`, {
        headers: headers,
      });

      if (response.data) {
        const readyStudios = response.data.filter(studio => studio.status === "READY");
        console.log('Runs - Setting studios to:', readyStudios.map(s => ({id: s.id, name: s.display_name})));
        setStudios(readyStudios);
      }
    } catch (error) {
      console.log('Runs - Error in getAvailableStudios:', error);
    }
  }

  async function fetchGroups(studioId) {
    if (!studioId) return;
    
    setIsLoadingGroups(true);
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/test/groups?studio_id=${studioId}`, {
        headers: headers,
      });

      if (response.data) {
        setGroups(response.data);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setIsLoadingGroups(false);
    }
  }

  useEffect(() => {
    getUser();
    getAvailableStudios();
  }, []);

  useEffect(() => {
    console.log('Runs - Studios useEffect triggered, studios.length:', studios.length);
    if (studios.length > 0) {
      // Try to get saved studio from localStorage
      const savedStudio = localStorage.getItem('selectedStudio');
      const savedStudioId = savedStudio ? parseInt(savedStudio, 10) : null;
      console.log('Runs - Retrieved from localStorage:', savedStudio, 'parsed as:', savedStudioId);
      console.log('Runs - Available studios:', studios.map(s => s.id));
      console.log('Runs - Current selectedStudio state:', selectedStudio);
      const savedStudioExists = savedStudioId && studios.find(studio => studio.id === savedStudioId);
      console.log('Runs - Saved studio exists:', !!savedStudioExists, savedStudioExists);
      
      if (savedStudioExists) {
        console.log('Runs - Using saved studio:', savedStudioId);
        setSelectedStudioWithLogging(savedStudioId);
      } else {
        console.log('Runs - Using first studio:', studios[0].id);
        setSelectedStudioWithLogging(studios[0].id);
      }
    }
  }, [studios]);

  useEffect(() => {
    console.log('Runs - selectedStudio useEffect triggered, selectedStudio:', selectedStudio);
    if (selectedStudio) {
      fetchGroups(selectedStudio);
    }
    setIsLoading(false);
  }, [selectedStudio]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy h:mm a");
    } catch {
      return dateString;
    }
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds || milliseconds === 0) return "0s";
    
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      const remainingHours = hours % 24;
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    }
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }
    
    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }
    
    return `${seconds}s`;
  };

  const calculatePassPercentage = (statusBreakdown) => {
    if (!statusBreakdown) return 0;
    
    const total = statusBreakdown.passed + statusBreakdown.failed + statusBreakdown.skipped;
    if (total === 0) return 0;
    
    return Number(((statusBreakdown.passed / total) * 100).toFixed(1));
  };

  const getStatusColor = (passPercentage) => {
    if (passPercentage >= 80) return "#4CAF50";
    if (passPercentage >= 50) return "#FF9800";
    return "#F44336";
  };

  const filterOldRunningTests = (groups) => {
    const sixHoursAgo = new Date();
    sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
    
    return groups.filter(group => {
      // If test has 0 tests (running) and is older than 6 hours, filter it out
      if (group.total_playwright_tests === 0) {
        const createdAt = new Date(group.created_at);
        return createdAt > sixHoursAgo;
      }
      // Keep all completed tests
      return true;
    });
  };

  const formatInsights = (insights) => {
    if (!insights || !Array.isArray(insights) || insights.length === 0) {
      return null;
    }
    
    return insights.map((insight, index) => {
      const severity = insight.severity?.toLowerCase() || "";
      const severityEmoji = severity === 'failure' ? '🚨' : severity === 'warning' ? '⚠️' : '⏭️';
      
      return (
        <Box key={index} sx={{ mb: index < insights.length - 1 ? 1 : 0 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#fff' }}>
            {severityEmoji} {insight.summary} ({insight.count} tests)
          </Typography>
        </Box>
      );
    });
  };

  const RunCard = ({ group }) => {
    const passPercentage = calculatePassPercentage(group.status_breakdown);
    const statusColor = getStatusColor(passPercentage);
    const isInProgress = group.total_playwright_tests === 0;

    return (
      <Card
        sx={{
          backgroundColor: "#1a1a1a",
          border: "1px solid #2a2a2a",
          borderRadius: 2,
          transition: "all 0.2s ease-in-out",
          cursor: isInProgress ? "default" : "pointer",
          "&:hover": isInProgress ? {} : {
            borderColor: "#fff",
            transform: "translateY(-2px)",
            boxShadow: "0 8px 24px rgba(255, 255, 255, 0.15)",
          },
        }}
        onClick={() => !isInProgress && navigate(`/test/group/${group.group_id}`)}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  color: "#888",
                  mb: 0.5,
                }}
              >
                {group.group_id.substring(0, 8)}...
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {formatDate(group.created_at)}
              </Typography>
            </Box>
            
            {/* Status Badge */}
            <Box>
              {isInProgress ? (
                <Chip
                  label="Running"
                  size="small"
                  icon={<AccessTime sx={{ fontSize: 16 }} />}
                  sx={{
                    backgroundColor: "#FFA72620",
                    color: "#FFA726",
                    fontWeight: 500,
                  }}
                />
              ) : (
                <Chip
                  label="Completed"
                  size="small"
                  icon={<CheckCircle sx={{ fontSize: 16, color: "#FFFFFF" }} />}
                  sx={{
                    backgroundColor: `${statusColor}20`,
                    color: statusColor,
                    fontWeight: 500,
                    "& .MuiChip-icon": {
                      color: "#FFFFFF !important",
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: "#2a2a2a", mb: 2 }} />

          {/* Metrics */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={3} sx={{ mb: 2, height: "60px" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: "#888", fontSize: "0.75rem", mb: 0.5 }}>
                  Total Tests
                </Typography>
                <Typography sx={{ 
                  color: "#fff", 
                  fontSize: "1.25rem", 
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                  {group.total_playwright_tests}
                </Typography>
              </Box>
              
              {!isInProgress && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: "#888", fontSize: "0.75rem", mb: 0.5 }}>
                    Pass Rate
                  </Typography>
                  <Typography sx={{ 
                    color: statusColor, 
                    fontSize: "1.25rem", 
                    fontWeight: 600,
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden"
                  }}>
                    {passPercentage}%
                    {passPercentage >= 80 ? 
                      <TrendingUp sx={{ fontSize: 18, color: "#4CAF50" }} /> : 
                      <TrendingDown sx={{ fontSize: 18, color: "#F44336" }} />
                    }
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: "#888", fontSize: "0.75rem", mb: 0.5 }}>
                  Duration
                </Typography>
                <Typography sx={{ 
                  color: "#fff", 
                  fontSize: "1.25rem", 
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                  {formatDuration(group.total_duration_ms) || "0s"}
                </Typography>
              </Box>
            </Stack>

            {/* Progress Bar */}
            {!isInProgress && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={passPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#2a2a2a",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: statusColor,
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            )}

            {/* Test Breakdown */}
            {!isInProgress && (
              <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1 }}>
                {group.status_breakdown.passed > 0 && (
                  <Chip
                    label={`${group.status_breakdown.passed} passed`}
                    size="small"
                    sx={{
                      backgroundColor: "#4CAF5020",
                      color: "#4CAF50",
                      fontSize: "0.75rem",
                    }}
                  />
                )}
                {group.status_breakdown.failed > 0 && (
                  <Chip
                    label={`${group.status_breakdown.failed} failed`}
                    size="small"
                    sx={{
                      backgroundColor: "#F4433620",
                      color: "#F44336",
                      fontSize: "0.75rem",
                    }}
                  />
                )}
                {group.status_breakdown.skipped > 0 && (
                  <Chip
                    label={`${group.status_breakdown.skipped} skipped`}
                    size="small"
                    sx={{
                      backgroundColor: "#75757520",
                      color: "#757575",
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>

          {/* Insights */}
          {group.insights && Array.isArray(group.insights) && group.insights.length > 0 && (
            <Box>
              <Divider sx={{ borderColor: "#2a2a2a", mb: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Lightbulb sx={{ fontSize: 16, color: "#FFA726" }} />
                <Typography sx={{ color: "#FFA726", fontSize: "0.75rem", fontWeight: 500 }}>
                  Insights ({group.insights.length})
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 80, overflow: "hidden" }}>
                {formatInsights(group.insights.slice(0, 2))}
                {group.insights.length > 2 && (
                  <Typography sx={{ color: "#666", fontSize: "0.75rem" }}>
                    +{group.insights.length - 2} more...
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <NavBar user={user} />
      <Box
        sx={{
          minHeight: "100vh",
          pt: 8,
          px: { xs: 2, sm: 3, md: 4 },
          ml: { xs: "64px", sm: "64px", md: "64px" },
          backgroundColor: "#121212",
        }}
      >
        <Box
          sx={{
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 3, md: 0 },
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: "#fff",
                  mb: 1,
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
                }}
              >
                🚀 Test Runs
              </Typography>
              <Typography sx={{ 
                color: "#888", 
                fontSize: { xs: "0.75rem", sm: "0.875rem" }
              }}>
                Monitor and manage your test execution runs
              </Typography>
            </Box>
            <FormControl sx={{ 
              minWidth: { xs: "100%", sm: 200 },
              maxWidth: { xs: "100%", sm: "none" }
            }}>
              <InputLabel
                id="studio-select-label"
                sx={{
                  color: "#888",
                  "&.Mui-focused": { color: "#fff" },
                }}
              >
                Select Studio
              </InputLabel>
              <Select
                labelId="studio-select-label"
                value={selectedStudio}
                onChange={(e) => {
                  const newStudio = e.target.value;
                  console.log('Runs - onChange triggered with:', newStudio);
                  setSelectedStudioWithLogging(newStudio);
                  localStorage.setItem('selectedStudio', newStudio);
                  console.log('Runs - Saved to localStorage:', localStorage.getItem('selectedStudio'));
                }}
                label="Select Studio"
                sx={{
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #2a2a2a",
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2a2a2a",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#333",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4a90e2",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
              >
                {studios.map((studio) => (
                  <MenuItem key={studio.id} value={studio.id}>
                    {studio.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
              }}
            >
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          ) : (
            <>
              {/* Runs Grid */}
              {isLoadingGroups ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <CircularProgress size={32} sx={{ color: "#fff" }} />
                </Box>
              ) : filterOldRunningTests(groups).length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                      xl: "repeat(4, 1fr)",
                    },
                    gap: 3,
                  }}
                >
                  {filterOldRunningTests(groups).map((group) => (
                    <RunCard key={group.group_id} group={group} />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: { xs: 4, sm: 6 },
                    textAlign: "center",
                    border: "1px dashed #333",
                    borderRadius: 2,
                    backgroundColor: "#1a1a1a",
                  }}
                >
                  <Typography sx={{ 
                    color: "#888", 
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    mb: 1
                  }}>
                    No test runs found for this studio.
                  </Typography>
                  <Typography sx={{ 
                    color: "#666", 
                    fontSize: { xs: "0.625rem", sm: "0.75rem" }
                  }}>
                    Test runs will appear here once you start executing tests.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Runs;